// packages/web-ui/src/state/onboarding.ts
//
// W2-S10 — `onboarding` zustand slice.
//
// Owns the first-launch onboarding-takeover state:
//   - `completed`        — boolean flag persisted to `localStorage`. Once `true`,
//                          the takeover is never re-shown for the same browser
//                          profile. (PRD FR-SHELL-1)
//   - `dismissedAt`      — ISO-8601 timestamp recording when the user finished
//                          or skipped the wizard. Useful for audit/debug.
//   - `currentStep`      — index 0..3 driving the stepper UI.
//   - `workspacePath`    — captured in step 1; downstream code may use it to
//                          seed the system-folder pick. The slice does not
//                          mutate the file-system itself.
//   - `provider`         — id chosen in step 2 (one of `azure | anthropic |
//                          openai | dev-playback`). The actual API-key write
//                          flows through the (existing) settings slice via
//                          a one-shot Settings batch-put when the wizard
//                          completes; the slice exposes a callback hook so
//                          host wiring can apply the real persistence.
//   - `apiKey`           — held in memory only while the wizard is open;
//                          cleared on close.
//   - `telemetryEnabled` — boolean, default `false`. Captured in step 3 and
//                          persisted to the user-config namespace via the
//                          settings batch-put on completion.
//
// Persistence model (keep it minimal — user-config writes go through the
// settings slice, not this slice's localStorage shim):
//   `localStorage['tessa.onboarding.completed']` = '1'   (only field this slice persists)
//   `localStorage['tessa.onboarding.dismissedAt']` = ISO timestamp
//
// The slice is a vanilla zustand store factory (matches `sessions.ts` /
// `settings.ts` / `utilityDrawer.ts` patterns) so the host component owns
// its lifecycle and tests can inject a clean store.

import { createStore, type StoreApi } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';

export type OnboardingProvider = 'azure' | 'anthropic' | 'openai' | 'dev-playback';

export type OnboardingStepIndex = 0 | 1 | 2 | 3;

export const ONBOARDING_STEP_COUNT = 4;

export interface OnboardingSliceState {
  /** True once the wizard has finished or been skipped. Persisted. */
  completed: boolean;
  /** ISO timestamp written when the wizard finishes/skips. Persisted. */
  dismissedAt: string | null;
  /** True while the takeover is mounted (open). Drives the gate. */
  open: boolean;
  /** Active step in the wizard, 0..3. */
  currentStep: OnboardingStepIndex;
  /** Step 1 — workspace path the user typed/picked. */
  workspacePath: string;
  /** Step 2 — provider selection. */
  provider: OnboardingProvider;
  /** Step 2 — API key (held in memory only, cleared on close). */
  apiKey: string;
  /** Step 3 — telemetry consent. Default false. */
  telemetryEnabled: boolean;
}

export interface OnboardingSliceActions {
  /** Mounts the takeover. Idempotent. */
  openWizard: () => void;
  /** Closes the takeover and persists `completed = true`. */
  finish: () => void;
  /** Closes the takeover via Skip. Equivalent to `finish()` for persistence. */
  skip: () => void;
  /** Move to a specific step (clamps to 0..3). */
  setStep: (idx: number) => void;
  /** Step forward; clamps at the last step. */
  next: () => void;
  /** Step backward; clamps at 0. */
  back: () => void;
  /** Setter for `workspacePath`. */
  setWorkspacePath: (path: string) => void;
  /** Setter for `provider`. */
  setProvider: (p: OnboardingProvider) => void;
  /** Setter for `apiKey`. */
  setApiKey: (key: string) => void;
  /** Setter for `telemetryEnabled`. */
  setTelemetryEnabled: (next: boolean) => void;
  /** Test/debug helper. */
  _reset: () => void;
}

export type OnboardingSlice = OnboardingSliceState & OnboardingSliceActions;

export const ONBOARDING_INITIAL_STATE: OnboardingSliceState = {
  completed: false,
  dismissedAt: null,
  open: false,
  currentStep: 0,
  workspacePath: '',
  provider: 'azure',
  apiKey: '',
  telemetryEnabled: false,
};

export const ONBOARDING_STORAGE_KEYS = {
  completed: 'tessa.onboarding.completed',
  dismissedAt: 'tessa.onboarding.dismissedAt',
} as const;

/**
 * Persistence shim — the slice is intentionally narrow about what hits
 * `localStorage`: only the `completed` flag and the dismissal timestamp.
 * All other onboarding state is in-memory and discarded on close.
 *
 * Tests inject a custom storage via `OnboardingSliceConfig.storage`.
 */
export interface OnboardingStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

function defaultStorage(): OnboardingStorage {
  if (typeof localStorage === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
    };
  }
  return {
    getItem: (k) => localStorage.getItem(k),
    setItem: (k, v) => {
      localStorage.setItem(k, v);
    },
    removeItem: (k) => {
      localStorage.removeItem(k);
    },
  };
}

export interface OnboardingSliceConfig {
  storage?: OnboardingStorage;
  /** Optional callback invoked AFTER `finish()`/`skip()` writes the flag. */
  onComplete?: (info: {
    skipped: boolean;
    workspacePath: string;
    provider: OnboardingProvider;
    apiKey: string;
    telemetryEnabled: boolean;
  }) => void;
  /** Override `Date.now` for deterministic test timestamps. */
  nowImpl?: () => Date;
}

function clampStep(idx: number): OnboardingStepIndex {
  if (idx < 0) return 0;
  if (idx > ONBOARDING_STEP_COUNT - 1) return (ONBOARDING_STEP_COUNT - 1) as OnboardingStepIndex;
  return idx as OnboardingStepIndex;
}

/** Zustand StateCreator for the onboarding slice. */
export const createOnboardingSlice =
  (config: OnboardingSliceConfig = {}): StateCreator<OnboardingSlice> =>
  (set, get) => {
    const storage = config.storage ?? defaultStorage();
    const now = config.nowImpl ?? (() => new Date());

    // Hydrate `completed` + `dismissedAt` from storage on creation.
    const persistedCompleted = storage.getItem(ONBOARDING_STORAGE_KEYS.completed) === '1';
    const persistedDismissedAt = storage.getItem(ONBOARDING_STORAGE_KEYS.dismissedAt);

    function persistCompletion(skipped: boolean): void {
      const ts = now().toISOString();
      storage.setItem(ONBOARDING_STORAGE_KEYS.completed, '1');
      storage.setItem(ONBOARDING_STORAGE_KEYS.dismissedAt, ts);
      const snapshot = get();
      set((s) => ({
        ...s,
        completed: true,
        dismissedAt: ts,
        open: false,
        // Wipe the API key — it's only used to seed downstream settings.
        apiKey: '',
      }));
      if (config.onComplete !== undefined) {
        config.onComplete({
          skipped,
          workspacePath: snapshot.workspacePath,
          provider: snapshot.provider,
          apiKey: snapshot.apiKey,
          telemetryEnabled: snapshot.telemetryEnabled,
        });
      }
    }

    return {
      ...ONBOARDING_INITIAL_STATE,
      completed: persistedCompleted,
      dismissedAt: persistedDismissedAt,

      openWizard: () => {
        set((s) => ({ ...s, open: true, currentStep: 0 }));
      },

      finish: () => {
        persistCompletion(false);
      },

      skip: () => {
        persistCompletion(true);
      },

      setStep: (idx) => {
        set((s) => ({ ...s, currentStep: clampStep(idx) }));
      },

      next: () => {
        set((s) => ({ ...s, currentStep: clampStep(s.currentStep + 1) }));
      },

      back: () => {
        set((s) => ({ ...s, currentStep: clampStep(s.currentStep - 1) }));
      },

      setWorkspacePath: (path) => {
        set((s) => ({ ...s, workspacePath: path }));
      },

      setProvider: (p) => {
        set((s) => ({ ...s, provider: p }));
      },

      setApiKey: (key) => {
        set((s) => ({ ...s, apiKey: key }));
      },

      setTelemetryEnabled: (next) => {
        set((s) => ({ ...s, telemetryEnabled: next }));
      },

      _reset: () => {
        storage.removeItem(ONBOARDING_STORAGE_KEYS.completed);
        storage.removeItem(ONBOARDING_STORAGE_KEYS.dismissedAt);
        set(() => ({ ...ONBOARDING_INITIAL_STATE }));
      },
    };
  };

/** Vanilla-store factory for standalone use (tests, takeover-internal call-sites). */
export function createOnboardingStore(
  config: OnboardingSliceConfig = {},
): StoreApi<OnboardingSlice> {
  return createStore<OnboardingSlice>()(createOnboardingSlice(config));
}

/** Pure selector — `true` when the takeover should be displayed. */
export function selectShouldShowTakeover(state: OnboardingSliceState): boolean {
  return !state.completed || state.open;
}

/** Pure selector — `true` when the user can advance from the current step. */
export function selectCanAdvance(state: OnboardingSliceState): boolean {
  switch (state.currentStep) {
    case 0:
      return state.workspacePath.trim().length > 0;
    case 1:
      // Dev playback needs no key.
      return state.provider === 'dev-playback' || state.apiKey.trim().length > 0;
    case 2:
      // Telemetry step — consent is optional, both states valid.
      return true;
    case 3:
      return true;
  }
}
